import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { PickDeep } from "type-fest";

import { CURRENT_LOCALE_ID } from "@intocare/angular-data/lib/current-locale-id";
import {
	evaluationMeasureConceptToDisplayWith,
	RestrictiveMeasureForResident,
	RestrictiveMeasuresForResident,
} from "@intocare/data-access/portal/care";
import { ValueSet } from "@intocare/datatypes/lib/value-set";
import {
	DataListComponent,
	DataListEmptyTemplateDirective,
	DataListItemTemplateDirective,
	DescriptionDetailsDirective,
	DescriptionItemDirective,
	DescriptionTermDirective,
	EmptyFeedbackComponent,
} from "@intocare/design/design-system-base";
import { DynamicDatePipe } from "@intocare/design/design-system-base/i18n";

import { EvaluationMeasureStatusTagComponent } from "../../shared/components/evaluation-measure-status-tag.component";

export type RestrictiveMeasureListItem = PickDeep<
	RestrictiveMeasureForResident<keyof RestrictiveMeasuresForResident>,
	| "followUpPlan.title"
	| "evaluation.evaluationDate"
	| "measure.measureId"
	| "measure.type"
	| "measure.subtype"
	| "measure.careTreeCode"
	| "measure.action"
	| "measure.details"
	| "measure.reason"
>;

@Component({
	selector: "ic-restrictive-measure-list",
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		DataListComponent,
		DataListItemTemplateDirective,
		TranslatePipe,
		DataListEmptyTemplateDirective,
		EmptyFeedbackComponent,
		DynamicDatePipe,
		EvaluationMeasureStatusTagComponent,
		DescriptionItemDirective,
		DescriptionDetailsDirective,
		DescriptionTermDirective,
	],
	templateUrl: "./restrictive-measure-list.component.html",
})
export class RestrictiveMeasureListComponent<T extends RestrictiveMeasureListItem = RestrictiveMeasureListItem> {
	private readonly currentLocaleId = inject(CURRENT_LOCALE_ID);

	readonly dataRestrictiveMeasures = input.required<T[]>();
	readonly dataCareTreeValueSet = input.required<ValueSet | undefined>();

	protected readonly getRestrictiveMeasureKey = (item: T) => item.measure.measureId;

	protected readonly evaluationMeasureConceptToDisplay = computed(() =>
		evaluationMeasureConceptToDisplayWith({
			locale: this.currentLocaleId(),
			careTreeValueSet: this.dataCareTreeValueSet(),
		}),
	);
}
